import { useParams, Link, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/feature/ArticleCard';
import { categories, allArticles, siteInfo } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const category = categories.find((c) => c.slug === slug);
  const categoryArticles = allArticles.filter((a) => a.categorySlug === slug);

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-foreground-950 mb-4">分类不存在</h2>
            <Link to="/" className="text-primary-500 hover:text-primary-600">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'https://www.heiliaochigua.com',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: category.name,
        item: `https://www.heiliaochigua.com/category/${category.slug}`,
      },
    ],
  };

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: `${category.name} - ${siteInfo.name}`,
    description: `${category.description}。${siteInfo.description}`,
    url: `https://www.heiliaochigua.com/category/${category.slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: siteInfo.name,
      url: 'https://www.heiliaochigua.com',
    },
    primaryImageOfPage: categoryArticles[0]?.imageUrl,
  };

  return (
    <>
      <Navbar />
      <main>
        <nav className="pt-20 pb-4 px-4 md:px-6 max-w-7xl mx-auto" aria-label="面包屑导航">
          <ol className="flex items-center gap-2 text-sm text-foreground-500">
            <li>
              <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
            </li>
            <li>
              <i className="ri-arrow-right-s-line text-xs"></i>
            </li>
            <li className="text-foreground-900 font-medium">{category.name}</li>
          </ol>
        </nav>

        <section className="py-8 md:py-14 bg-background-100">
          <div className="px-4 md:px-6 max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row items-start gap-8">
              <div className="lg:w-[40%]">
                <p className="text-xs font-semibold uppercase tracking-widest text-foreground-400 mb-2">
                  当前分类
                </p>
              </div>
              <div className="lg:w-[60%]">
                <h1 className="text-3xl md:text-4xl font-black font-heading text-foreground-950 mb-3">
                  {category.name}
                </h1>
                <p className="text-lg text-foreground-600 leading-relaxed">
                  {category.description}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="py-4 bg-background-50 border-b border-background-200/70 sticky top-16 z-40">
          <div className="px-4 md:px-6 max-w-7xl mx-auto">
            <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide">
              {categories.map((cat) => {
                const isActive = cat.slug === slug;
                return (
                  <button
                    key={cat.slug}
                    type="button"
                    onClick={() => navigate(`/category/${cat.slug}`)}
                    className={`px-4 py-2 text-sm font-medium rounded-full whitespace-nowrap transition-all duration-200 cursor-pointer shrink-0 ${
                      isActive
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-700 hover:bg-background-200 hover:text-foreground-900'
                    }`}
                  >
                    {cat.name}
                  </button>
                );
              })}
            </div>
          </div>
        </section>

        <section className="py-10 md:py-16 bg-background-50">
          <div className="px-4 md:px-6 max-w-7xl mx-auto">
            {categoryArticles.length > 0 ? (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                  {categoryArticles.map((article) => (
                    <ArticleCard
                      key={article.id}
                      slug={article.slug}
                      title={article.title}
                      excerpt={article.excerpt}
                      category={article.category}
                      categorySlug={article.categorySlug}
                      imageUrl={article.imageUrl}
                      author={article.author}
                      avatar={article.avatar}
                      publishDate={article.publishDate}
                      readCount={article.readCount}
                      isHot={article.isHot}
                      isExclusive={article.isExclusive}
                    />
                  ))}
                </div>

                <div className="flex justify-center gap-2 mt-10">
                  <button
                    type="button"
                    className="px-5 py-2.5 text-sm font-semibold rounded-full border border-foreground-300 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    加载更多
                  </button>
                </div>
              </>
            ) : (
              <div className="text-center py-20">
                <i className="ri-inbox-line text-5xl text-foreground-300 mb-4 block"></i>
                <p className="text-foreground-500 text-lg">该分类暂无内容，敬请期待</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageSchema) }}
      />
    </>
  );
}